import {
  IntegrationError,
  PreconditionError,
} from '@buf/dentechse_patient-api-gateway.grpc_web/api/patient/v1/request_failure_pb'
import { ContactInformation } from '../features/contactInformationScreenSlice'
import { DataLayerEventName, MessageType } from '../models/enums'
import {
  Appointment,
  BankIDMessage,
  BookingCompletedEventObject,
  BookingConfirmationEventObject,
  BookingContactInfoEventObject,
  BookingFailedEventObject,
  BookingPayload,
  BookingSummaryEventObject,
  CheckoutEventObject,
  FilterAppointmentEventObject,
  FilterCityEventObject,
  FilterClinicEventObject,
  FilterTreatmentEventObject,
  Message,
  PhoneNumberClickedEventObject,
  TransactionEventObject,
  Treatment,
  EventMessageDataLayer,
  TokenizationSession,
  LoadTokenizationScreenEventObject,
  TokenizationErrorEventObject,
  TokenizationCancelEventObject,
  TokenizationCompleteEventObject,
  WidgetDisplayedEventObject,
  SourceEvent,
  ChooseUpcomingBookingObject,
  ChoosePreviousBookingObject,
  ChangeMyBookingClickedObject,
  CancelMyBookingClickedObject,
  OpenPromoSectionObject,
  CheckPromoCodeEventObject,
  ValidPromoCodeEventObject,
  InvalidPromoCodeEventObject,
} from '../models/types'
import {
  bookingContactInfo,
  bookingComplete,
  bookingFormLoaded,
  filterByCity,
  filterByTreatment,
  filterByClinic,
  filterByAppointment,
  bookingSummary,
  bookingInfo,
  bookingConfirmation,
  bookingFailed,
  phoneNumberClicked,
  loadTokenizationScreen,
  tokenizationError,
  tokenizationCancel,
  tokenizationComplete,
  widgetDisplayed,
  pushToDataLayer,
  chooseUpcomingBooking,
  choosePreviousBooking,
  changeMyBookingClicked,
  cancelMyBookingClicked,
  openPromoSection,
  checkPromoCode,
  validPromoCode,
  invalidPromoCode,
} from './dataLayerEvents'
import { uuidv4 } from './uuidv4'
import AdyenCheckoutError from '@adyen/adyen-web/dist/types/core/Errors/AdyenCheckoutError'

export type ErrorType =
  | keyof typeof PreconditionError
  | keyof typeof IntegrationError

export interface BookingData {
  treatmentID: string
  treatmentName: string
  treatmentPrice: number
  clinicName: string
  caregiverName: string
  bookingId: string
  promoCode?: string
}

export interface BookingCompletedFacebookData {
  appointmentName: string
  clinicName: string
  appointmentID: string
  firstName: string
  lastName: string
  emailAddress: string
  phoneNumber: string
  bookingId: string
  promoCode?: string
}

export interface BookingFailedData {
  appointmentName: string
  clinicName: string
  appointmentID: string
  firstName?: string
  lastName?: string
  emailAddress?: string
  phoneNumber?: string
  connectionId?: string
  externalBookingId?: string
  promoCode?: string
  errorType: ErrorType | unknown
}

export interface ClinicEventData {
  id: string
  name: string
  address: string
  city: string
  latLng:
    | {
        latitude: number
        longitude: number
      }
    | undefined
  subCity: string
}

/**
 * Sends a message to the parent window
 * @param message - The message to send, either a Message or BankIDMessage
 */
export const sendMessageToParentWindow = (message: Message | BankIDMessage) => {
  const hostWindow = window.parent

  try {
    hostWindow.postMessage(message, '*')
  } catch {
    // Do nothing
  }
}

/**
 * Handles BankID related messages from a MessageEvent
 * @param event - The MessageEvent containing BankID message data
 */
export const bankIdMessageHandler = (event: MessageEvent<BankIDMessage>) => {
  sendMessageToParentWindow(event.data)
}

/**
 * Sends a message to the parent window or handles the message
 * @param message - The message to send, either a Message or BankIDMessage
 */
export const sendOrHandleMessage = (message: Message | BankIDMessage) => {
  if ('type' in message && 'message' in message) {
    messageTypeHandler(message.type, message.message)
  }

  sendMessageToParentWindow(message)
}

/**
 * Handles different types of messages based on the MessageType
 * @param type - The type of message to handle
 * @param dataMessage - The data payload of the message
 */
export const messageTypeHandler = (type: MessageType, dataMessage: object) => {
  switch (type) {
    case MessageType.BookingFormLoaded: {
      const message = dataMessage as CheckoutEventObject
      bookingFormLoaded(message)
      break
    }
    case MessageType.BookingComplete: {
      const message = dataMessage as TransactionEventObject
      bookingContactInfo(message)
      break
    }
    case MessageType.BookingCompleteFacebook: {
      const message = dataMessage as BookingCompletedEventObject
      bookingComplete(message)
      break
    }
    case MessageType.BookingFailed: {
      const message = dataMessage as BookingFailedEventObject
      bookingFailed(message)
      break
    }
    case MessageType.ChooseCity: {
      const message = dataMessage as FilterCityEventObject
      filterByCity(message)
      break
    }
    case MessageType.ChooseTreatment: {
      const message = dataMessage as FilterTreatmentEventObject
      filterByTreatment(message)
      break
    }
    case MessageType.ChooseClinic: {
      const message = dataMessage as FilterClinicEventObject
      filterByClinic(message)
      break
    }
    case MessageType.ChooseAppointment: {
      const message = dataMessage as FilterAppointmentEventObject
      filterByAppointment(message)
      break
    }
    case MessageType.ShowBookingSummary: {
      const message = dataMessage as BookingSummaryEventObject
      bookingSummary(message)
      break
    }
    case MessageType.AddContactInfo: {
      const message = dataMessage as BookingContactInfoEventObject
      bookingInfo(message)
      break
    }
    case MessageType.ShowBookingConfirmation: {
      const message = dataMessage as BookingConfirmationEventObject
      bookingConfirmation(message)
      break
    }
    case MessageType.PhoneNumberClicked: {
      const message = dataMessage as PhoneNumberClickedEventObject
      phoneNumberClicked(message)
      break
    }
    case MessageType.LoadTokenizationScreen: {
      const message = dataMessage as LoadTokenizationScreenEventObject
      loadTokenizationScreen(message)
      break
    }
    case MessageType.TokenizationError: {
      const message = dataMessage as TokenizationErrorEventObject
      tokenizationError(message)
      break
    }
    case MessageType.TokenizationCancel: {
      const message = dataMessage as TokenizationCancelEventObject
      tokenizationCancel(message)
      break
    }
    case MessageType.TokenizationComplete: {
      const message = dataMessage as TokenizationCompleteEventObject
      tokenizationComplete(message)
      break
    }
    case MessageType.WidgetDisplayed: {
      const message = dataMessage as WidgetDisplayedEventObject
      widgetDisplayed(message)
      break
    }
    case MessageType.ChooseUpcomingBooking: {
      const message = dataMessage as ChooseUpcomingBookingObject
      chooseUpcomingBooking(message)
      break
    }
    case MessageType.ChoosePreviousBooking: {
      const message = dataMessage as ChoosePreviousBookingObject
      choosePreviousBooking(message)
      break
    }
    case MessageType.ChangeMyBookingClicked: {
      const message = dataMessage as ChangeMyBookingClickedObject
      changeMyBookingClicked(message)
      break
    }
    case MessageType.CancelMyBookingClicked: {
      const message = dataMessage as CancelMyBookingClickedObject
      cancelMyBookingClicked(message)
      break
    }
    case MessageType.OpenPromoSection: {
      const message = dataMessage as OpenPromoSectionObject
      openPromoSection(message)
      break
    }
    case MessageType.CheckPromoCode: {
      const message = dataMessage as CheckPromoCodeEventObject
      checkPromoCode(message)
      break
    }
    case MessageType.ValidPromoCode: {
      const message = dataMessage as ValidPromoCodeEventObject
      validPromoCode(message)
      break
    }
    case MessageType.InvalidPromoCode: {
      const message = dataMessage as InvalidPromoCodeEventObject
      invalidPromoCode(message)
      break
    }
  }
}

/**
 * Handles messages from the parent window
 * @param event - The MessageEvent containing the message data
 */
export const handleTypeMessageFromParentWindow = (
  event: MessageEvent<Message>
) => {
  switch (event.data.type) {
    case MessageType.BookingFormLoaded: {
      pushToDataLayer(event.data.message)
      break
    }
    case MessageType.BookingComplete: {
      pushToDataLayer(event.data.message)
      break
    }
    case MessageType.BookingCompleteFacebook: {
      pushToDataLayer(event.data.message)
      break
    }
  }
}

/**
 * Sends a message to resize the frame
 * @param height - The desired height of the frame
 * @param width - The desired width of the frame
 */
export const senMessagedResizeFrame = (height: number, width: number) => {
  const message: Message = {
    type: MessageType.ResizeFrame,
    message: {
      height,
      width,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends a booking form loaded event to the parent window
 * @param eventData - The booking data containing treatment and clinic information
 * @screen `BookingContactScreen` - The screens of the usage
 */
export const sendBookingFormLoaded = (eventData: BookingData) => {
  const {
    treatmentID,
    treatmentName,
    treatmentPrice,
    clinicName,
    caregiverName,
  } = eventData

  const message: Message = {
    type: MessageType.BookingFormLoaded,
    message: {
      event: DataLayerEventName.Checkout,
      ecommerce: {
        checkout: {
          item_variant: {
            caregiverName: caregiverName,
          },
          products: [
            {
              id: treatmentID,
              name: treatmentName,
              price: treatmentPrice,
              brand: clinicName,
              quantity: 1,
            },
          ],
        },
      },
    },
  }

  sendOrHandleMessage(message)
}

/**
 * Sends a booking completed message to the parent window.
 *
 * @param eventData - The data related to the completed booking.
 * @param eventData.appointmentName - The name of the appointment.
 * @param eventData.clinicName - The name of the clinic.
 * @param eventData.appointmentID - The ID of the appointment.
 * @param eventData.firstName - The first name of the patient.
 * @param eventData.lastName - The last name of the patient.
 * @param eventData.emailAddress - The email address of the patient.
 * @param eventData.phoneNumber - The phone number of the patient.
 * @param eventData.bookingId - The ID of the booking.
 * @param eventData.errorType - The type of error that occurred.
 * @screen `BookingContactScreen` - The screens of the usage
 */
export const sendBookingFailed = (eventData: BookingFailedData) => {
  const {
    appointmentName,
    clinicName,
    appointmentID,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    errorType,
    connectionId,
    externalBookingId,
    promoCode,
  } = eventData

  const message: Message = {
    type: MessageType.BookingFailed,
    message: {
      event: DataLayerEventName.BookingFailed,
      booking: {
        service: {
          appointment: appointmentName,
          id: appointmentID,
          clinic: clinicName,
          connectionId: connectionId || '',
          externalBookingId: externalBookingId || '',
          promoCode: promoCode || '',
        },
        user_data: {
          email_address: emailAddress || '',
          phone_number: phoneNumber || '',
          first_name: firstName || '',
          last_name: lastName || '',
        },
        errorType: errorType,
      },
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends a booking completion message to the parent window.
 *
 * @param eventData - The data related to the completed booking
 * @param eventData.treatmentID - The ID of the treatment
 * @param eventData.treatmentName - The name of the treatment
 * @param eventData.treatmentPrice - The price of the treatment
 * @param eventData.clinicName - The name of the clinic
 * @screen `BookingConfirmationScreen` - The screens of the usage
 */
export const sendBookingCompleted = (eventData: BookingData) => {
  const { treatmentID, treatmentName, treatmentPrice, clinicName, bookingId } =
    eventData

  const message: Message = {
    type: MessageType.BookingComplete,
    message: {
      event: DataLayerEventName.Transaction,
      bookingId,
      ecommerce: {
        purchase: {
          actionField: {
            id: uuidv4(),
            revenue: treatmentPrice,
          },
          products: [
            {
              id: treatmentID,
              name: treatmentName,
              price: treatmentPrice,
              brand: clinicName,
              quantity: 1,
            },
          ],
        },
      },
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends a booking completed message to the parent window.
 *
 * @param eventData - The data related to the completed booking.
 * @param eventData.treatmentID - The ID of the treatment.
 * @param eventData.treatmentName - The name of the treatment.
 * @param eventData.treatmentPrice - The price of the treatment.
 * @param eventData.clinicName - The name of the clinic.
 * @screen `BookingConfirmationScreen` - The screens of the usage
 */
export const sendBookingCompletedFacebook = (
  eventData: BookingCompletedFacebookData
) => {
  const {
    appointmentName,
    clinicName,
    appointmentID,
    firstName,
    lastName,
    emailAddress,
    phoneNumber,
    bookingId,
    promoCode,
  } = eventData

  const message: Message = {
    type: MessageType.BookingCompleteFacebook,
    message: {
      event: DataLayerEventName.BookingCompleted,
      booking: {
        service: {
          appointment: appointmentName,
          id: appointmentID,
          clinic: clinicName,
          bookingId,
          promoCode,
        },
        user_data: {
          email_address: emailAddress,
          phone_number: phoneNumber,
          first_name: firstName,
          last_name: lastName,
        },
      },
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends a booking confirmation event to the parent window
 * @screen `BookingConfirmationScreen` - The screens of the usage
 */
export const sendBookingConfirmation = (bookingId: string) => {
  const message: Message = {
    type: MessageType.ShowBookingConfirmation,
    message: {
      event: DataLayerEventName.BookingConfirmation,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends information about the selected city to the parent window
 * @param cityName - The name of the selected city
 * @screen `CityScreen` - The screens of the usage
 */
export const sendSelectedCity = (cityName: string) => {
  const message: Message = {
    type: MessageType.ChooseCity,
    message: {
      event: DataLayerEventName.FilterCity,
      city: cityName,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends information about the selected treatment to the parent window
 * @param treatment - The selected treatment object
 * @screen `TreatmentScreen` | `TreatmentSelectionScreen` - The screens of the usage
 */
export const sendSelectedTreatment = (treatment: Treatment) => {
  const message: Message = {
    type: MessageType.ChooseTreatment,
    message: {
      event: DataLayerEventName.FilterTreatment,
      treatment,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends information about the selected clinic to the parent window
 * @param clinic - The selected clinic data
 * @screen `ClinicScreen` - The screens of the usage
 */
export const sendSelectedClinic = (clinic: ClinicEventData) => {
  const message: Message = {
    type: MessageType.ChooseClinic,
    message: {
      event: DataLayerEventName.FilterClinic,
      clinic,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends information about the selected appointment to the parent window
 * @param appointment - The selected appointment object
 * @screen `AppointmentScreen` | `ClinicScreen` - The screens of the usage
 */
export const sendSelectedAppointment = (appointment: Appointment) => {
  const message: Message = {
    type: MessageType.ChooseAppointment,
    message: {
      event: DataLayerEventName.FilterAppointment,
      appointment,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends booking summary information to the parent window
 * @param booking - The booking payload containing all booking details
 * @screen `BookingSummaryScreen` - The screens of the usage
 */
export const sendBookingSummary = (booking: BookingPayload) => {
  const message: Message = {
    type: MessageType.ShowBookingSummary,
    message: {
      event: DataLayerEventName.BookingSummary,
      booking,
    },
  }
  sendOrHandleMessage(message)
}

/**
 * Sends contact information for the booking to the parent window
 * @param contactInformation - The contact information provided by the user
 * @screen `BookingContactScreen` - The screens of the usage
 */
export const sendBookingContactInfo = (
  contactInformation: ContactInformation,
  bookingId: string
) => {
  const message: Message = {
    type: MessageType.AddContactInfo,
    message: {
      event: DataLayerEventName.BookingContactInfo,
      contactInformation,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

export const sendPhoneNumberClicked = (
  phoneNumber: string,
  source: SourceEvent
) => {
  const message: Message = {
    type: MessageType.PhoneNumberClicked,
    message: {
      event: DataLayerEventName.PhoneNumberClicked,
      phoneNumber,
      source,
    },
  }
  sendOrHandleMessage(message)
}

export const sendWidgetDisplayed = () => {
  const message: EventMessageDataLayer = {
    type: MessageType.WidgetDisplayed,
    message: {
      event: DataLayerEventName.WidgetDisplayed,
    },
  }
  sendOrHandleMessage(message)
}

export const sendLoadTokenizationScreen = (session: TokenizationSession) => {
  const message: Message = {
    type: MessageType.LoadTokenizationScreen,
    message: {
      event: DataLayerEventName.LoadTokenizationScreen,
      session,
    },
  }
  sendOrHandleMessage(message)
}

export const sendTokenizationError = (
  error: AdyenCheckoutError,
  session: TokenizationSession
) => {
  const message: Message = {
    type: MessageType.TokenizationError,
    message: {
      event: DataLayerEventName.TokenizationError,
      error,
      session,
    },
  }
  sendOrHandleMessage(message)
}

export const sendTokenizationCancel = (session: TokenizationSession) => {
  const message: Message = {
    type: MessageType.TokenizationCancel,
    message: {
      event: DataLayerEventName.TokenizationCancel,
      session,
    },
  }
  sendOrHandleMessage(message)
}

export const sendTokenizationComplete = (session: TokenizationSession) => {
  const message: Message = {
    type: MessageType.TokenizationComplete,
    message: {
      event: DataLayerEventName.TokenizationComplete,
      session,
    },
  }
  sendOrHandleMessage(message)
}

export const sendChooseUpcomingBooking = (bookingId: string) => {
  const message: Message = {
    type: MessageType.ChooseUpcomingBooking,
    message: {
      event: DataLayerEventName.ChooseUpcomingBooking,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

export const sendChoosePreviousBooking = (bookingId: string) => {
  const message: Message = {
    type: MessageType.ChoosePreviousBooking,
    message: {
      event: DataLayerEventName.ChoosePreviousBooking,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

export const sendChangeMyBookingClicked = (bookingId: string) => {
  const message: Message = {
    type: MessageType.ChangeMyBookingClicked,
    message: {
      event: DataLayerEventName.ChangeMyBookingClicked,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

export const sendCancelMyBookingClicked = (bookingId: string) => {
  const message: Message = {
    type: MessageType.CancelMyBookingClicked,
    message: {
      event: DataLayerEventName.CancelMyBookingClicked,
      bookingId,
    },
  }
  sendOrHandleMessage(message)
}

export const sendOpenPromoSection = () => {
  const message: Message = {
    type: MessageType.OpenPromoSection,
    message: {
      event: DataLayerEventName.OpenPromoSection,
    },
  }
  sendOrHandleMessage(message)
}

export const sendCheckPromoCode = (promoCode: string) => {
  const message: Message = {
    type: MessageType.CheckPromoCode,
    message: {
      event: DataLayerEventName.CheckPromoCode,
      promoCode,
    },
  }
  sendOrHandleMessage(message)
}

export const sendValidPromoCode = (
  promoCode: string,
  finalPrice: number,
  originalPrice: number
) => {
  const message: Message = {
    type: MessageType.ValidPromoCode,
    message: {
      event: DataLayerEventName.ValidPromoCode,
      promoCode,
      finalPrice,
      originalPrice,
    },
  }
  sendOrHandleMessage(message)
}

export const sendInvalidPromoCode = (promoCode: string, error: string) => {
  const message: Message = {
    type: MessageType.InvalidPromoCode,
    message: {
      event: DataLayerEventName.InvalidPromoCode,
      promoCode,
      error,
    },
  }
  sendOrHandleMessage(message)
}
